'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function RetirementCalculator() {
  const t = useTranslations("tools.retirementCalculator");
  const [screenSize, setScreenSize] = useState('mobile');
  const [activeTab, setActiveTab] = useState('tab1');

  // Tab 1 - How Much to Save
  const [tab1Inputs, setTab1Inputs] = useState({
    currentAge: 35,
    retirementAge: 67,
    lifeExpectancy: 85,
    currentIncome: 70000,
    incomeIncrease: 3,
    retirementIncomePercent: 75,
    investmentReturn: 6,
    inflationRate: 3,
    currentSavings: 30000,
    monthlyContribution: 500,
    otherIncome: 0,
  });
  const [tab1Results, setTab1Results] = useState<any>(null);

  // Tab 2 - Savings Plan
  const [tab2Inputs, setTab2Inputs] = useState({
    currentAge: 35,
    retirementAge: 67,
    targetAmount: 600000,
    currentSavings: 30000,
    investmentReturn: 6,
  });
  const [tab2Results, setTab2Results] = useState<any>(null);

  // Tab 3 - Monthly Withdrawal
  const [tab3Inputs, setTab3Inputs] = useState({
    currentAge: 35,
    retirementAge: 67,
    lifeExpectancy: 85,
    currentSavings: 30000,
    monthlyContribution: 500,
    investmentReturn: 6,
    inflationRate: 3,
  });
  const [tab3Results, setTab3Results] = useState<any>(null);

  // Tab 4 - How Long Will It Last
  const [tab4Inputs, setTab4Inputs] = useState({
    currentAmount: 600000,
    monthlyWithdrawal: 5000,
    investmentReturn: 6,
  });
  const [tab4Results, setTab4Results] = useState<any>(null);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const formatCurrency = (value: number) => {
    return '$' + Math.round(value).toLocaleString('en-US');
  };

  const formatNumber = (value: number) => {
    return Math.round(value).toLocaleString('en-US');
  };

  const handleTab1Calculate = (e: React.FormEvent) => {
    e.preventDefault();

    const {
      currentAge,
      retirementAge,
      lifeExpectancy,
      currentIncome,
      incomeIncrease,
      retirementIncomePercent,
      investmentReturn,
      inflationRate,
      currentSavings,
      monthlyContribution,
      otherIncome,
    } = tab1Inputs;

    const yearsToRetirement = retirementAge - currentAge;
    const yearsInRetirement = lifeExpectancy - retirementAge;

    const futureIncome = currentIncome * Math.pow(1 + incomeIncrease / 100, yearsToRetirement);
    const retirementIncome = futureIncome * (retirementIncomePercent / 100);
    const monthlyRetirementIncome = retirementIncome / 12;
    const netMonthlyNeed = monthlyRetirementIncome - otherIncome;

    const realReturn = (1 + investmentReturn / 100) / (1 + inflationRate / 100) - 1;

    let totalNeeded = 0;
    if (realReturn > 0.0001) {
      totalNeeded = netMonthlyNeed * 12 * (1 - Math.pow(1 + realReturn, -yearsInRetirement)) / realReturn;
    } else {
      totalNeeded = netMonthlyNeed * 12 * yearsInRetirement;
    }

    const monthlyRate = investmentReturn / 100 / 12;
    const totalMonths = yearsToRetirement * 12;

    let projectedSavings = currentSavings * Math.pow(1 + monthlyRate, totalMonths);

    if (monthlyRate > 0.0001) {
      projectedSavings += monthlyContribution * (Math.pow(1 + monthlyRate, totalMonths) - 1) / monthlyRate;
    } else {
      projectedSavings += monthlyContribution * totalMonths;
    }

    const shortfall = projectedSavings - totalNeeded;
    const readinessPercent = Math.min((projectedSavings / totalNeeded) * 100, 100);

    setTab1Results({
      totalNeeded,
      projectedSavings,
      monthlyRetirementIncome,
      shortfall,
      readinessPercent,
    });
  };

  const handleTab2Calculate = (e: React.FormEvent) => {
    e.preventDefault();

    const {
      currentAge,
      retirementAge,
      targetAmount,
      currentSavings,
      investmentReturn,
    } = tab2Inputs;

    const yearsToRetirement = retirementAge - currentAge;
    const monthlyRate = investmentReturn / 100 / 12;
    const totalMonths = yearsToRetirement * 12;

    const futureValueOfCurrent = currentSavings * Math.pow(1 + monthlyRate, totalMonths);
    const amountNeeded = targetAmount - futureValueOfCurrent;

    let monthlyContribution = 0;
    if (monthlyRate > 0.0001) {
      monthlyContribution = amountNeeded * monthlyRate / (Math.pow(1 + monthlyRate, totalMonths) - 1);
    } else {
      monthlyContribution = amountNeeded / totalMonths;
    }

    const annualContribution = monthlyContribution * 12;
    const totalContributions = monthlyContribution * totalMonths;
    const investmentGrowth = targetAmount - currentSavings - totalContributions;

    setTab2Results({
      monthlyContribution,
      annualContribution,
      totalContributions,
      investmentGrowth,
    });
  };

  const handleTab3Calculate = (e: React.FormEvent) => {
    e.preventDefault();

    const {
      currentAge,
      retirementAge,
      lifeExpectancy,
      currentSavings,
      monthlyContribution,
      investmentReturn,
      inflationRate,
    } = tab3Inputs;

    const yearsToRetirement = retirementAge - currentAge;
    const yearsInRetirement = lifeExpectancy - retirementAge;
    const monthlyRate = investmentReturn / 100 / 12;
    const totalMonths = yearsToRetirement * 12;

    let totalAtRetirement = currentSavings * Math.pow(1 + monthlyRate, totalMonths);

    if (monthlyRate > 0.0001) {
      totalAtRetirement += monthlyContribution * (Math.pow(1 + monthlyRate, totalMonths) - 1) / monthlyRate;
    } else {
      totalAtRetirement += monthlyContribution * totalMonths;
    }

    const realReturn = (1 + investmentReturn / 100) / (1 + inflationRate / 100) - 1;
    const monthlyRealReturn = realReturn / 12;
    const retirementMonths = yearsInRetirement * 12;

    let monthlyWithdrawal = 0;
    if (monthlyRealReturn > 0.0001) {
      monthlyWithdrawal = totalAtRetirement * monthlyRealReturn / (1 - Math.pow(1 + monthlyRealReturn, -retirementMonths));
    } else {
      monthlyWithdrawal = totalAtRetirement / retirementMonths;
    }

    const annualWithdrawal = monthlyWithdrawal * 12;

    setTab3Results({
      monthlyWithdrawal,
      totalAtRetirement,
      yearsInRetirement,
      annualWithdrawal,
    });
  };

  const handleTab4Calculate = (e: React.FormEvent) => {
    e.preventDefault();

    const {
      currentAmount,
      monthlyWithdrawal,
      investmentReturn,
    } = tab4Inputs;

    const monthlyRate = investmentReturn / 100 / 12;

    let months = 0;
    let balance = currentAmount;
    let totalWithdrawals = 0;

    while (balance > 0 && months < 1200) {
      balance = balance * (1 + monthlyRate) - monthlyWithdrawal;
      totalWithdrawals += monthlyWithdrawal;
      months++;

      if (balance < 0) {
        totalWithdrawals += balance;
        balance = 0;
      }
    }

    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;
    const investmentEarnings = totalWithdrawals - currentAmount;

    setTab4Results({
      years,
      months,
      remainingMonths,
      totalWithdrawals,
      investmentEarnings,
    });
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    { marginBottom: '1.5rem' },
    { marginBottom: '2rem' },
    { marginBottom: '2.5rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      marginBottom: '2rem',
      borderRadius: '1rem',
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { maxWidth: '100%', margin: '0 auto' },
    {},
    { maxWidth: '900px' },
    { maxWidth: '1000px' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '1.5rem' },
    {},
    { marginBottom: '2rem' },
    { marginBottom: '2.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const calculatorTabsStyle = getResponsiveStyle(
    { display: 'flex', gap: '1rem', marginBottom: '2rem', flexWrap: 'wrap' as const, justifyContent: 'center' as const },
    { flexDirection: 'column' as const },
    { flexDirection: 'row' as const },
    { flexDirection: 'row' as const }
  );

  const tabBtnStyle = (isActive: boolean) => getResponsiveStyle(
    {
      padding: '0.75rem 1.5rem',
      background: isActive ? 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)' : 'white',
      border: isActive ? 'none' : '2px solid #e2e8f0',
      borderRadius: '0.75rem',
      cursor: 'pointer',
      fontSize: '0.95rem',
      fontWeight: 600,
      color: isActive ? 'white' : '#64748b',
      transition: 'all 0.3s ease',
    },
    { width: '100%' },
    { width: 'auto' },
    { width: 'auto' }
  );

  const calculatorFormStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      marginBottom: '2rem',
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const formSectionStyle = {
    marginBottom: '1.5rem',
  };

  const sectionTitleStyle = {
    fontSize: '1.1rem',
    fontWeight: 600,
    color: '#2563eb',
    marginBottom: '1rem',
    paddingBottom: '0.5rem',
    borderBottom: '2px solid #e0f2fe',
  };

  const formGridStyle = getResponsiveStyle(
    { display: 'grid', gap: '1.25rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(2, 1fr)' },
    { gridTemplateColumns: 'repeat(2, 1fr)' }
  );

  const formGroupStyle = {
    display: 'flex',
    flexDirection: 'column' as const,
  };

  const labelStyle = {
    fontSize: '0.9rem',
    fontWeight: 600,
    color: '#334155',
    marginBottom: '0.5rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  };

  const tooltipIconStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '18px',
    height: '18px',
    background: '#e0f2fe',
    color: '#2563eb',
    borderRadius: '50%',
    fontSize: '0.75rem',
    cursor: 'help',
    fontWeight: 700,
  };

  const inputWrapperStyle = {
    position: 'relative' as const,
    display: 'flex',
    alignItems: 'center',
  };

  const inputPrefixStyle = {
    position: 'absolute' as const,
    left: '1rem',
    color: '#64748b',
    fontWeight: 600,
  };

  const inputSuffixStyle = {
    position: 'absolute' as const,
    right: '1rem',
    color: '#64748b',
    fontSize: '0.9rem',
  };

  const inputStyle = getResponsiveStyle(
    {
      width: '100%',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      fontSize: '1rem',
      transition: 'all 0.3s ease',
      background: 'white',
    },
    { padding: '0.75rem 1rem' },
    { padding: '0.875rem 1.25rem' },
    { padding: '1rem 1.5rem' }
  );

  const inputWithPrefixStyle = {
    ...inputStyle,
    paddingLeft: '2rem',
  };

  const inputWithSuffixStyle = {
    ...inputStyle,
    paddingRight: '3rem',
  };

  const inputWithBothStyle = {
    ...inputStyle,
    paddingLeft: '2rem',
    paddingRight: '3rem',
  };

  const calculateBtnStyle = getResponsiveStyle(
    {
      width: '100%',
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '0.75rem',
      fontWeight: 700,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      marginTop: '1.5rem',
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '1rem 2rem', fontSize: '1.125rem' },
    { padding: '1.25rem 2.5rem', fontSize: '1.25rem' }
  );

  const resultsStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const resultsH3Style = getResponsiveStyle(
    { textAlign: 'center' as const, color: colors.textPrimary, marginBottom: '1.5rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' }
  );

  const resultGridStyle = getResponsiveStyle(
    { display: 'grid', gap: '1.25rem', marginBottom: '2rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(2, 1fr)' },
    { gridTemplateColumns: 'repeat(2, 1fr)' }
  );

  const resultCardStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
      borderRadius: '0.75rem',
      borderLeft: '4px solid #2563eb',
    },
    { padding: '1rem' },
    { padding: '1.25rem' },
    { padding: '1.5rem' }
  );

  const resultCardHighlightStyle = {
    ...resultCardStyle,
    background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
    borderLeftColor: '#f59e0b',
  };

  const resultLabelStyle = {
    fontSize: '0.85rem',
    color: '#64748b',
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    display: 'block',
    marginBottom: '0.5rem',
  };

  const resultValueStyle = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, display: 'block' },
    { fontSize: '1.5rem' },
    { fontSize: '1.625rem' },
    { fontSize: '1.75rem' }
  );

  const chartContainerStyle = getResponsiveStyle(
    {
      marginTop: '2rem',
      background: '#f8fafc',
      borderRadius: '0.75rem',
    },
    { padding: '1rem' },
    { padding: '1.25rem' },
    { padding: '1.5rem' }
  );

  const chartTitleStyle = {
    fontSize: '1.1rem',
    fontWeight: 600,
    color: '#334155',
    marginBottom: '1rem',
  };

  const progressBarStyle = {
    width: '100%',
    height: '2rem',
    background: '#e2e8f0',
    borderRadius: '1rem',
    overflow: 'hidden' as const,
    position: 'relative' as const,
  };

  const progressFillStyle = (width: number) => ({
    height: '100%',
    background: 'linear-gradient(90deg, #2563eb 0%, #7c3aed 100%)',
    transition: 'width 1s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end' as const,
    paddingRight: '1rem',
    color: 'white',
    fontWeight: 700,
    fontSize: '0.9rem',
    width: `${width}%`,
  });

  const glassCardStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.6)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: '1px solid rgba(255, 200, 180, 0.2)',
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem',
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const listStyle = {
    marginLeft: '1.5rem',
    marginBottom: '1rem',
  };

  const listItemStyle = getResponsiveStyle(
    { marginBottom: '0.75rem', color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const strongStyle = {
    fontWeight: 600,
    color: colors.textPrimary,
  };

  const faqItemStyle = {
    marginBottom: '1.5rem',
  };

  const faqQuestionStyle = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const faqAnswerStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' }
  );

  const referenceStyle = {
    fontSize: '0.875rem',
    color: colors.textSecondary,
    marginBottom: '0.5rem',
  };

  const linkStyle = {
    color: colors.primary,
    textDecoration: 'none',
    wordBreak: 'break-all' as const,
  };

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t("title")}</h1>
        <p style={summaryTextStyle}>{t("subtitle")}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={h2Style}>🎯 {t("toolTitle")}</h2>
          </div>

          <div style={calculatorTabsStyle}>
            <button
              style={tabBtnStyle(activeTab === 'tab1')}
              onClick={() => setActiveTab('tab1')}
            >
              {t("tab1Title")}
            </button>
            <button
              style={tabBtnStyle(activeTab === 'tab2')}
              onClick={() => setActiveTab('tab2')}
            >
              {t("tab2Title")}
            </button>
            <button
              style={tabBtnStyle(activeTab === 'tab3')}
              onClick={() => setActiveTab('tab3')}
            >
              {t("tab3Title")}
            </button>
            <button
              style={tabBtnStyle(activeTab === 'tab4')}
              onClick={() => setActiveTab('tab4')}
            >
              {t("tab4Title")}
            </button>
          </div>

          {activeTab === 'tab1' && (
            <div>
              <form style={calculatorFormStyle} onSubmit={handleTab1Calculate}>
                <div style={formSectionStyle}>
                  <div style={sectionTitleStyle}>{t("basicInfoSection")}</div>
                  <div style={formGridStyle}>
                    <div style={formGroupStyle}>
                      <label style={labelStyle}>{t("currentAge")}</label>
                      <input
                        type="number"
                        style={inputStyle}
                        value={tab1Inputs.currentAge}
                        onChange={(e) => setTab1Inputs({...tab1Inputs, currentAge: parseFloat(e.target.value)})}
                        min="18"
                        max="100"
                        required
                      />
                    </div>
                    <div style={formGroupStyle}>
                      <label style={labelStyle}>{t("retirementAge")}</label>
                      <input
                        type="number"
                        style={inputStyle}
                        value={tab1Inputs.retirementAge}
                        onChange={(e) => setTab1Inputs({...tab1Inputs, retirementAge: parseFloat(e.target.value)})}
                        min="50"
                        max="100"
                        required
                      />
                    </div>
                    <div style={formGroupStyle}>
                      <label style={labelStyle}>
                        {t("lifeExpectancy")}
                        <span style={tooltipIconStyle} title={t("lifeExpectancyTooltip")}>?</span>
                      </label>
                      <input
                        type="number"
                        style={inputStyle}
                        value={tab1Inputs.lifeExpectancy}
                        onChange={(e) => setTab1Inputs({...tab1Inputs, lifeExpectancy: parseFloat(e.target.value)})}
                        min="60"
                        max="120"
                        required
                      />
                    </div>
                    <div style={formGroupStyle}>
                      <label style={labelStyle}>{t("currentIncome")}</label>
                      <div style={inputWrapperStyle}>
                        <span style={inputPrefixStyle}>$</span>
                        <input
                          type="number"
                          style={inputWithBothStyle}
                          value={tab1Inputs.currentIncome}
                          onChange={(e) => setTab1Inputs({...tab1Inputs, currentIncome: parseFloat(e.target.value)})}
                          min="0"
                          step="1000"
                          required
                        />
                        <span style={inputSuffixStyle}>/year</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div style={formSectionStyle}>
                  <div style={sectionTitleStyle}>{t("assumptionsSection")}</div>
                  <div style={formGridStyle}>
                    <div style={formGroupStyle}>
                      <label style={labelStyle}>{t("incomeIncrease")}</label>
                      <div style={inputWrapperStyle}>
                        <input
                          type="number"
                          style={inputWithSuffixStyle}
                          value={tab1Inputs.incomeIncrease}
                          onChange={(e) => setTab1Inputs({...tab1Inputs, incomeIncrease: parseFloat(e.target.value)})}
                          min="0"
                          max="20"
                          step="0.1"
                          required
                        />
                        <span style={inputSuffixStyle}>%</span>
                      </div>
                    </div>
                    <div style={formGroupStyle}>
                      <label style={labelStyle}>
                        {t("retirementIncomePercent")}
                        <span style={tooltipIconStyle} title={t("retirementIncomeTooltip")}>?</span>
                      </label>
                      <div style={inputWrapperStyle}>
                        <input
                          type="number"
                          style={inputWithSuffixStyle}
                          value={tab1Inputs.retirementIncomePercent}
                          onChange={(e) => setTab1Inputs({...tab1Inputs, retirementIncomePercent: parseFloat(e.target.value)})}
                          min="0"
                          max="100"
                          step="1"
                          required
                        />
                        <span style={inputSuffixStyle}>%</span>
                      </div>
                    </div>
                    <div style={formGroupStyle}>
                      <label style={labelStyle}>{t("investmentReturn")}</label>
                      <div style={inputWrapperStyle}>
                        <input
                          type="number"
                          style={inputWithSuffixStyle}
                          value={tab1Inputs.investmentReturn}
                          onChange={(e) => setTab1Inputs({...tab1Inputs, investmentReturn: parseFloat(e.target.value)})}
                          min="0"
                          max="20"
                          step="0.1"
                          required
                        />
                        <span style={inputSuffixStyle}>%</span>
                      </div>
                    </div>
                    <div style={formGroupStyle}>
                      <label style={labelStyle}>
                        {t("inflationRate")}
                        <span style={tooltipIconStyle} title={t("inflationTooltip")}>?</span>
                      </label>
                      <div style={inputWrapperStyle}>
                        <input
                          type="number"
                          style={inputWithSuffixStyle}
                          value={tab1Inputs.inflationRate}
                          onChange={(e) => setTab1Inputs({...tab1Inputs, inflationRate: parseFloat(e.target.value)})}
                          min="0"
                          max="10"
                          step="0.1"
                          required
                        />
                        <span style={inputSuffixStyle}>%</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div style={formSectionStyle}>
                  <div style={sectionTitleStyle}>{t("currentSavingsSection")}</div>
                  <div style={formGridStyle}>
                    <div style={formGroupStyle}>
                      <label style={labelStyle}>{t("currentSavings")}</label>
                      <div style={inputWrapperStyle}>
                        <span style={inputPrefixStyle}>$</span>
                        <input
                          type="number"
                          style={inputWithPrefixStyle}
                          value={tab1Inputs.currentSavings}
                          onChange={(e) => setTab1Inputs({...tab1Inputs, currentSavings: parseFloat(e.target.value)})}
                          min="0"
                          step="1000"
                          required
                        />
                      </div>
                    </div>
                    <div style={formGroupStyle}>
                      <label style={labelStyle}>{t("monthlyContribution")}</label>
                      <div style={inputWrapperStyle}>
                        <span style={inputPrefixStyle}>$</span>
                        <input
                          type="number"
                          style={inputWithPrefixStyle}
                          value={tab1Inputs.monthlyContribution}
                          onChange={(e) => setTab1Inputs({...tab1Inputs, monthlyContribution: parseFloat(e.target.value)})}
                          min="0"
                          step="50"
                          required
                        />
                      </div>
                    </div>
                    <div style={formGroupStyle}>
                      <label style={labelStyle}>
                        {t("otherIncome")}
                        <span style={tooltipIconStyle} title={t("otherIncomeTooltip")}>?</span>
                      </label>
                      <div style={inputWrapperStyle}>
                        <span style={inputPrefixStyle}>$</span>
                        <input
                          type="number"
                          style={inputWithPrefixStyle}
                          value={tab1Inputs.otherIncome}
                          onChange={(e) => setTab1Inputs({...tab1Inputs, otherIncome: parseFloat(e.target.value)})}
                          min="0"
                          step="100"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <button type="submit" style={calculateBtnStyle}>{t("calculateButton")}</button>
              </form>

              {tab1Results && (
                <div style={resultsStyle}>
                  <h3 style={resultsH3Style}>📊 {t("tab1ResultsTitle")}</h3>
                  <div style={resultGridStyle}>
                    <div style={resultCardHighlightStyle}>
                      <span style={resultLabelStyle}>{t("totalNeeded")}</span>
                      <span style={resultValueStyle}>{formatCurrency(tab1Results.totalNeeded)}</span>
                    </div>
                    <div style={resultCardStyle}>
                      <span style={resultLabelStyle}>{t("projectedSavings")}</span>
                      <span style={resultValueStyle}>{formatCurrency(tab1Results.projectedSavings)}</span>
                    </div>
                    <div style={resultCardStyle}>
                      <span style={resultLabelStyle}>{t("monthlyIncome")}</span>
                      <span style={resultValueStyle}>{formatCurrency(tab1Results.monthlyRetirementIncome)}</span>
                    </div>
                    <div style={resultCardStyle}>
                      <span style={resultLabelStyle}>{t("shortfall")}</span>
                      <span style={{...resultValueStyle, color: tab1Results.shortfall >= 0 ? '#10b981' : '#ef4444'}}>
                        {formatCurrency(tab1Results.shortfall)}
                      </span>
                    </div>
                  </div>
                  <div style={chartContainerStyle}>
                    <div style={chartTitleStyle}>{t("readinessTitle")}</div>
                    <div style={progressBarStyle}>
                      <div style={progressFillStyle(tab1Results.readinessPercent)}>
                        {Math.round(tab1Results.readinessPercent)}%
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'tab2' && (
            <div>
              <form style={calculatorFormStyle} onSubmit={handleTab2Calculate}>
                <div style={formSectionStyle}>
                  <div style={formGridStyle}>
                    <div style={formGroupStyle}>
                      <label style={labelStyle}>{t("currentAge")}</label>
                      <input
                        type="number"
                        style={inputStyle}
                        value={tab2Inputs.currentAge}
                        onChange={(e) => setTab2Inputs({...tab2Inputs, currentAge: parseFloat(e.target.value)})}
                        min="18"
                        max="100"
                        required
                      />
                    </div>
                    <div style={formGroupStyle}>
                      <label style={labelStyle}>{t("retirementAge")}</label>
                      <input
                        type="number"
                        style={inputStyle}
                        value={tab2Inputs.retirementAge}
                        onChange={(e) => setTab2Inputs({...tab2Inputs, retirementAge: parseFloat(e.target.value)})}
                        min="50"
                        max="100"
                        required
                      />
                    </div>
                    <div style={formGroupStyle}>
                      <label style={labelStyle}>{t("targetAmount")}</label>
                      <div style={inputWrapperStyle}>
                        <span style={inputPrefixStyle}>$</span>
                        <input
                          type="number"
                          style={inputWithPrefixStyle}
                          value={tab2Inputs.targetAmount}
                          onChange={(e) => setTab2Inputs({...tab2Inputs, targetAmount: parseFloat(e.target.value)})}
                          min="0"
                          step="10000"
                          required
                        />
                      </div>
                    </div>
                    <div style={formGroupStyle}>
                      <label style={labelStyle}>{t("currentSavings")}</label>
                      <div style={inputWrapperStyle}>
                        <span style={inputPrefixStyle}>$</span>
                        <input
                          type="number"
                          style={inputWithPrefixStyle}
                          value={tab2Inputs.currentSavings}
                          onChange={(e) => setTab2Inputs({...tab2Inputs, currentSavings: parseFloat(e.target.value)})}
                          min="0"
                          step="1000"
                          required
                        />
                      </div>
                    </div>
                    <div style={formGroupStyle}>
                      <label style={labelStyle}>{t("investmentReturn")}</label>
                      <div style={inputWrapperStyle}>
                        <input
                          type="number"
                          style={inputWithSuffixStyle}
                          value={tab2Inputs.investmentReturn}
                          onChange={(e) => setTab2Inputs({...tab2Inputs, investmentReturn: parseFloat(e.target.value)})}
                          min="0"
                          max="20"
                          step="0.1"
                          required
                        />
                        <span style={inputSuffixStyle}>%</span>
                      </div>
                    </div>
                  </div>
                </div>

                <button type="submit" style={calculateBtnStyle}>{t("calculateButton")}</button>
              </form>

              {tab2Results && (
                <div style={resultsStyle}>
                  <h3 style={resultsH3Style}>💰 {t("tab2ResultsTitle")}</h3>
                  <div style={resultGridStyle}>
                    <div style={resultCardHighlightStyle}>
                      <span style={resultLabelStyle}>{t("monthlyNeeded")}</span>
                      <span style={resultValueStyle}>{formatCurrency(tab2Results.monthlyContribution)}</span>
                    </div>
                    <div style={resultCardStyle}>
                      <span style={resultLabelStyle}>{t("annualNeeded")}</span>
                      <span style={resultValueStyle}>{formatCurrency(tab2Results.annualContribution)}</span>
                    </div>
                    <div style={resultCardStyle}>
                      <span style={resultLabelStyle}>{t("totalContributions")}</span>
                      <span style={resultValueStyle}>{formatCurrency(tab2Results.totalContributions)}</span>
                    </div>
                    <div style={resultCardStyle}>
                      <span style={resultLabelStyle}>{t("investmentGrowth")}</span>
                      <span style={resultValueStyle}>{formatCurrency(tab2Results.investmentGrowth)}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'tab3' && (
            <div>
              <form style={calculatorFormStyle} onSubmit={handleTab3Calculate}>
                <div style={formSectionStyle}>
                  <div style={formGridStyle}>
                    <div style={formGroupStyle}>
                      <label style={labelStyle}>{t("currentAge")}</label>
                      <input
                        type="number"
                        style={inputStyle}
                        value={tab3Inputs.currentAge}
                        onChange={(e) => setTab3Inputs({...tab3Inputs, currentAge: parseFloat(e.target.value)})}
                        min="18"
                        max="100"
                        required
                      />
                    </div>
                    <div style={formGroupStyle}>
                      <label style={labelStyle}>{t("retirementAge")}</label>
                      <input
                        type="number"
                        style={inputStyle}
                        value={tab3Inputs.retirementAge}
                        onChange={(e) => setTab3Inputs({...tab3Inputs, retirementAge: parseFloat(e.target.value)})}
                        min="50"
                        max="100"
                        required
                      />
                    </div>
                    <div style={formGroupStyle}>
                      <label style={labelStyle}>{t("lifeExpectancy")}</label>
                      <input
                        type="number"
                        style={inputStyle}
                        value={tab3Inputs.lifeExpectancy}
                        onChange={(e) => setTab3Inputs({...tab3Inputs, lifeExpectancy: parseFloat(e.target.value)})}
                        min="60"
                        max="120"
                        required
                      />
                    </div>
                    <div style={formGroupStyle}>
                      <label style={labelStyle}>{t("currentSavings")}</label>
                      <div style={inputWrapperStyle}>
                        <span style={inputPrefixStyle}>$</span>
                        <input
                          type="number"
                          style={inputWithPrefixStyle}
                          value={tab3Inputs.currentSavings}
                          onChange={(e) => setTab3Inputs({...tab3Inputs, currentSavings: parseFloat(e.target.value)})}
                          min="0"
                          step="1000"
                          required
                        />
                      </div>
                    </div>
                    <div style={formGroupStyle}>
                      <label style={labelStyle}>{t("monthlyContribution")}</label>
                      <div style={inputWrapperStyle}>
                        <span style={inputPrefixStyle}>$</span>
                        <input
                          type="number"
                          style={inputWithPrefixStyle}
                          value={tab3Inputs.monthlyContribution}
                          onChange={(e) => setTab3Inputs({...tab3Inputs, monthlyContribution: parseFloat(e.target.value)})}
                          min="0"
                          step="50"
                          required
                        />
                      </div>
                    </div>
                    <div style={formGroupStyle}>
                      <label style={labelStyle}>{t("investmentReturn")}</label>
                      <div style={inputWrapperStyle}>
                        <input
                          type="number"
                          style={inputWithSuffixStyle}
                          value={tab3Inputs.investmentReturn}
                          onChange={(e) => setTab3Inputs({...tab3Inputs, investmentReturn: parseFloat(e.target.value)})}
                          min="0"
                          max="20"
                          step="0.1"
                          required
                        />
                        <span style={inputSuffixStyle}>%</span>
                      </div>
                    </div>
                    <div style={formGroupStyle}>
                      <label style={labelStyle}>{t("inflationRate")}</label>
                      <div style={inputWrapperStyle}>
                        <input
                          type="number"
                          style={inputWithSuffixStyle}
                          value={tab3Inputs.inflationRate}
                          onChange={(e) => setTab3Inputs({...tab3Inputs, inflationRate: parseFloat(e.target.value)})}
                          min="0"
                          max="10"
                          step="0.1"
                          required
                        />
                        <span style={inputSuffixStyle}>%</span>
                      </div>
                    </div>
                  </div>
                </div>

                <button type="submit" style={calculateBtnStyle}>{t("calculateButton")}</button>
              </form>

              {tab3Results && (
                <div style={resultsStyle}>
                  <h3 style={resultsH3Style}>💵 {t("tab3ResultsTitle")}</h3>
                  <div style={resultGridStyle}>
                    <div style={resultCardHighlightStyle}>
                      <span style={resultLabelStyle}>{t("monthlyWithdrawal")}</span>
                      <span style={resultValueStyle}>{formatCurrency(tab3Results.monthlyWithdrawal)}</span>
                    </div>
                    <div style={resultCardStyle}>
                      <span style={resultLabelStyle}>{t("totalAtRetirement")}</span>
                      <span style={resultValueStyle}>{formatCurrency(tab3Results.totalAtRetirement)}</span>
                    </div>
                    <div style={resultCardStyle}>
                      <span style={resultLabelStyle}>{t("yearsInRetirement")}</span>
                      <span style={resultValueStyle}>{formatNumber(tab3Results.yearsInRetirement)} years</span>
                    </div>
                    <div style={resultCardStyle}>
                      <span style={resultLabelStyle}>{t("annualWithdrawal")}</span>
                      <span style={resultValueStyle}>{formatCurrency(tab3Results.annualWithdrawal)}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'tab4' && (
            <div>
              <form style={calculatorFormStyle} onSubmit={handleTab4Calculate}>
                <div style={formSectionStyle}>
                  <div style={formGridStyle}>
                    <div style={formGroupStyle}>
                      <label style={labelStyle}>{t("currentAmount")}</label>
                      <div style={inputWrapperStyle}>
                        <span style={inputPrefixStyle}>$</span>
                        <input
                          type="number"
                          style={inputWithPrefixStyle}
                          value={tab4Inputs.currentAmount}
                          onChange={(e) => setTab4Inputs({...tab4Inputs, currentAmount: parseFloat(e.target.value)})}
                          min="0"
                          step="10000"
                          required
                        />
                      </div>
                    </div>
                    <div style={formGroupStyle}>
                      <label style={labelStyle}>{t("monthlyWithdrawal")}</label>
                      <div style={inputWrapperStyle}>
                        <span style={inputPrefixStyle}>$</span>
                        <input
                          type="number"
                          style={inputWithPrefixStyle}
                          value={tab4Inputs.monthlyWithdrawal}
                          onChange={(e) => setTab4Inputs({...tab4Inputs, monthlyWithdrawal: parseFloat(e.target.value)})}
                          min="0"
                          step="100"
                          required
                        />
                      </div>
                    </div>
                    <div style={formGroupStyle}>
                      <label style={labelStyle}>{t("investmentReturn")}</label>
                      <div style={inputWrapperStyle}>
                        <input
                          type="number"
                          style={inputWithSuffixStyle}
                          value={tab4Inputs.investmentReturn}
                          onChange={(e) => setTab4Inputs({...tab4Inputs, investmentReturn: parseFloat(e.target.value)})}
                          min="0"
                          max="20"
                          step="0.1"
                          required
                        />
                        <span style={inputSuffixStyle}>%</span>
                      </div>
                    </div>
                  </div>
                </div>

                <button type="submit" style={calculateBtnStyle}>{t("calculateButton")}</button>
              </form>

              {tab4Results && (
                <div style={resultsStyle}>
                  <h3 style={resultsH3Style}>⏳ {t("tab4ResultsTitle")}</h3>
                  <div style={resultGridStyle}>
                    <div style={resultCardHighlightStyle}>
                      <span style={resultLabelStyle}>{t("yearsLast")}</span>
                      <span style={resultValueStyle}>{tab4Results.years} years, {tab4Results.remainingMonths} months</span>
                    </div>
                    <div style={resultCardStyle}>
                      <span style={resultLabelStyle}>{t("monthsLast")}</span>
                      <span style={resultValueStyle}>{formatNumber(tab4Results.months)} months</span>
                    </div>
                    <div style={resultCardStyle}>
                      <span style={resultLabelStyle}>{t("totalWithdrawals")}</span>
                      <span style={resultValueStyle}>{formatCurrency(tab4Results.totalWithdrawals)}</span>
                    </div>
                    <div style={resultCardStyle}>
                      <span style={resultLabelStyle}>{t("investmentEarnings")}</span>
                      <span style={resultValueStyle}>{formatCurrency(tab4Results.investmentEarnings)}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("introTitle")}</h2>
        <p style={paragraphStyle}>{t("introPara1")}</p>
        <p style={paragraphStyle}>{t("introPara2")}</p>
        <p style={paragraphStyle}>{t("introPara3")}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("howToUseTitle")}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}><strong style={strongStyle}>{t("step1Title")}</strong> {t("step1Desc")}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t("step2Title")}</strong> {t("step2Desc")}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t("step3Title")}</strong> {t("step3Desc")}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t("step4Title")}</strong> {t("step4Desc")}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("insightsTitle")}</h2>
        <p style={paragraphStyle}>{t("insightsPara1")}</p>
        <ul style={listStyle}>
          <li style={listItemStyle}><strong style={strongStyle}>{t("insight1Title")}</strong> {t("insight1Desc")}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t("insight2Title")}</strong> {t("insight2Desc")}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t("insight3Title")}</strong> {t("insight3Desc")}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t("insight4Title")}</strong> {t("insight4Desc")}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t("insight5Title")}</strong> {t("insight5Desc")}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("detailsTitle")}</h2>
        <p style={paragraphStyle}>{t("detailsPara1")}</p>
        <p style={paragraphStyle}>{t("detailsPara2")}</p>
        <p style={paragraphStyle}>{t("detailsPara3")}</p>
        <p style={paragraphStyle}>{t("detailsPara4")}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("faqTitle")}</h2>
        <div style={faqItemStyle}>
          <div style={faqQuestionStyle}>{t("faq1Question")}</div>
          <p style={faqAnswerStyle}>{t("faq1Answer")}</p>
        </div>
        <div style={faqItemStyle}>
          <div style={faqQuestionStyle}>{t("faq2Question")}</div>
          <p style={faqAnswerStyle}>{t("faq2Answer")}</p>
        </div>
        <div style={faqItemStyle}>
          <div style={faqQuestionStyle}>{t("faq3Question")}</div>
          <p style={faqAnswerStyle}>{t("faq3Answer")}</p>
        </div>
        <div style={faqItemStyle}>
          <div style={faqQuestionStyle}>{t("faq4Question")}</div>
          <p style={faqAnswerStyle}>{t("faq4Answer")}</p>
        </div>
        <div style={faqItemStyle}>
          <div style={faqQuestionStyle}>{t("faq5Question")}</div>
          <p style={faqAnswerStyle}>{t("faq5Answer")}</p>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("referencesTitle")}</h2>
        <p style={referenceStyle}>
          [1] <a href="https://www.fidelity.com/calculators-tools/retirement-calculator" style={linkStyle} target="_blank" rel="noopener noreferrer">Fidelity Retirement Calculator</a>
        </p>
        <p style={referenceStyle}>
          [2] <a href="https://www.ssa.gov/benefits/retirement/planner/AnypiaApplet.html" style={linkStyle} target="_blank" rel="noopener noreferrer">SSA Detailed Calculator (2025.2)</a>
        </p>
        <p style={referenceStyle}>
          [3] <a href="https://investor.vanguard.com/calculator-tools/retirement-income-calculator" style={linkStyle} target="_blank" rel="noopener noreferrer">Vanguard Retirement Income Calculator</a>
        </p>
        <p style={referenceStyle}>
          [4] <a href="https://www.investopedia.com/retirement-calculator-5085117" style={linkStyle} target="_blank" rel="noopener noreferrer">Investopedia: Best Retirement Calculators</a>
        </p>
        <p style={referenceStyle}>
          [5] <a href="https://www.forbes.com/advisor/retirement/retirement-calculator/" style={linkStyle} target="_blank" rel="noopener noreferrer">Forbes: Retirement Calculator Guide</a>
        </p>
      </section>
    </div>
  );
}
